import { Component } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { PremiseService } from '../_services/premise.service';
import { ToastrService } from 'ngx-toastr';
import { Location } from '@angular/common';
import { faCircleArrowLeft, faHomeAlt } from '@fortawesome/free-solid-svg-icons';
import { Notice, NoticeService } from '../_services';
import { PremiseAssociation } from '../_models/premise_association';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-notices',
    templateUrl: './notices.component.html',
    styleUrls: ['./notices.component.css'],
    standalone: false
})
export class NoticesComponent {
  faCircleArrowLeft = faCircleArrowLeft;
  faHomeAlt = faHomeAlt;

  loading = false;
  searchQuery = '';
  premiseAssociations: PremiseAssociation[] = [];

  propertyNotices: Notice[] = [];
  premisesNotices: Notice[] = [];

  propertyLabel = '';
  unitLabel = '';

  constructor(private toastr: ToastrService,
    private premiseService: PremiseService,
    private noticeService: NoticeService,
    private _location: Location,
    private router: Router) {

    }

  ngOnInit(): void {
    this.loading = true;

    this.premiseService.fetchUserAssociation().subscribe({
      next: (associations) => {
        this.premiseAssociations = associations || [];

        if (this.premiseAssociations.length === 0) {
          this.loading = false;
          this.router.navigate(['/premises/noassociation']);
          return;
        }

        const uniquePropertyIds = [...new Set(this.premiseAssociations.map((a) => a.propertyId))];

        this.propertyLabel = uniquePropertyIds.length === 1
          ? (localStorage.getItem('currentProperty') ? JSON.parse(localStorage.getItem('currentProperty') || '{}').name || `Property ${uniquePropertyIds[0]}` : `Property ${uniquePropertyIds[0]}`)
          : 'Multiple Properties';

        this.unitLabel = this.premiseAssociations.length === 1
          ? `${this.premiseAssociations[0].unit_number}`
          : 'Multiple Units';

        const propertyRequests = uniquePropertyIds.map((propertyId) => this.noticeService.getNoticesByPropertyId(propertyId));
        const premisesRequests = this.premiseAssociations.map((association) =>
          this.noticeService.getNoticesByPropertyIdAndUnitNumber(association.propertyId, association.unit_number)
        );

        forkJoin({
          propertyNoticeGroups: forkJoin(propertyRequests),
          premisesNoticeGroups: forkJoin(premisesRequests),
        }).subscribe({
          next: ({ propertyNoticeGroups, premisesNoticeGroups }) => {
            this.propertyNotices = this.deduplicateNoticesById(propertyNoticeGroups.flat());
            this.premisesNotices = this.deduplicateNoticesById(premisesNoticeGroups.flat());
            this.loading = false;
          },
          error: (error: HttpErrorResponse) => {
            this.loading = false;
            this.toastr.error('Failed to retrieve notices.', 'Error');
            console.log('Notice fetch error: ', error);
          },
        });
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        this.toastr.error('Something went wrong. Failed to fetch premise associations.', 'Error');
        console.log('Association fetch error: ', error);
      },
    });
  }

  goBack() {
    this._location.back();
  }


  goHome(): void {
    this.router.navigate(['/home'])
  }

  get filteredPropertyNotices(): Notice[] {
    return this.filterNotices(this.propertyNotices);
  }

  get filteredPremisesNotices(): Notice[] {
    return this.filterNotices(this.premisesNotices);
  }

  private filterNotices(notices: Notice[]): Notice[] {
    const query = this.searchQuery.trim().toLowerCase();

    if (!query) {
      return notices;
    }

    return notices.filter((notice) => {
      const searchable = [
        notice.shortDescription,
        notice.longDescription,
        `${notice.unit_number}`,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchable.includes(query);
    });
  }

  private deduplicateNoticesById(notices: Notice[]): Notice[] {
    const seen = new Set<number>();
    return (notices || []).filter((notice) => {
      if (!notice.id) {
        return true;
      }

      if (seen.has(notice.id)) {
        return false;
      }

      seen.add(notice.id);
      return true;
    });
  }
}
